import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Badge,
  IconButton,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import ReplayIcon from "@mui/icons-material/Replay";
import QuizPlaceholder from "../../../../assets/images/quiz-placeholder.jpg";
import "./QuizCard.css";
import useAxios from "../../../../hooks/useAxios";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../../context/AuthContext";
import Tooltip from "@mui/material/Tooltip";

const QuizCard = ({
  quiz,
  isStudent = false,
  onEditClick,
  onDeleteClick,
  onReload,
  onTakeQuiz,
  showAttachmentStatus = false,
  isActiveInClass = true,
  attachedAt = null,
}) => {
  const formattedDate = new Date(quiz.created_at).toLocaleString();
  const navigate = useNavigate();
  const axiosInstance = useAxios();
  const { status } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const handleStartQuiz = async (quizId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(`/sessions/create`, {
        quiz_id: quizId,
      });

      if (response.data.success) {
        // Navigate to the session page with the session ID
        navigate(`/sessions/${response.data.session.id}`);
      } else {
        setError(
          response.data.message || "Failed to start quiz. Please try again."
        );
      }
    } catch (err) {
      console.error("Error starting quiz session:", err);
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleActivateQuiz = async (quizId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(`/quizzes/${quizId}/activate`);
      if (response.data.success) {
        // Trigger the parent page reload if provided
        if (onReload) {
          onReload();
        }
      } else {
        setError(
          response.data.message ||
            "Failed to activate Assessment. Please try again."
        );
      }
    } catch (err) {
      console.error("Error activating Assessment:", err);
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Fetching Data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        // minHeight="100vh"
        textAlign="center"
      >
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
      </Box>
    );
  } else {
    return (
      <Card className="quiz-card">
        <Box className="card-header">
          {quiz.is_trial === 1 && (
            <Tooltip title={status === "active" ? "Activate Assessment" : ""}>
              <Badge
                className="trial-badge clickable" // Add a clickable class
                badgeContent="Trial"
                color="secondary"
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                onClick={
                  status === "active" ? () => handleActivateQuiz(quiz.id) : null
                }
                style={{ cursor: status === "active" ? "pointer" : "default" }}
              />
            </Tooltip>
          )}
          {/* <CardMedia
              component="img"
              image={quiz.cover_image ? `${process.env.REACT_APP_API_BASE_IMAGE_URL}${quiz.cover_image}` : QuizPlaceholder}
              alt="Quiz Cover"
              className="quiz-card-media"
            /> */}
          <Box className="card-header" position="relative">
            <CardMedia
              component="img"
              image={
                quiz.cover_image
                  ? `${process.env.REACT_APP_API_BASE_IMAGE_URL}${quiz.cover_image}`
                  : QuizPlaceholder
              }
              alt="Assessment Cover"
              className={`quiz-card-media ${
                isStudent && quiz.attempts_count > 0 ? "blurred-image" : ""
              }`}
            />
            {isStudent && quiz.attempts_count > 0 && (
              <Box className="overlay-text">
                <Typography variant="body2" className="overlay-message">
                  You already took this Assessment
                </Typography>
                <Typography variant="body2" className="overlay-message">
                  Evaluation: {quiz.sessions_data[0].final_evaluation}%
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <CardContent className="card-content">
          <Typography variant="h6" className="quiz-identifier">
            <span className="id-title">Title: </span>
            {quiz.title}
          </Typography>
          <Typography variant="body2" className="quiz-class">
            {quiz.class_name
              ? `Class: ${quiz.class_name}`
              : !quiz.is_trial && !isStudent && ""}
            {quiz.class
              ? `Class: ${quiz.class.class_name}`
              : !quiz.is_trial && !isStudent && !quiz.class_name && ""}
          </Typography>
          {isStudent && (
            <Typography variant="h6" className="quiz-identifier">
              <span className="id-title">Created By:</span>{" "}
              <span className="capitalize-text">{quiz.owner_name}</span>
            </Typography>
          )}
          {!isStudent && (
            <Typography variant="body2" className="quiz-date">
              Created at: {formattedDate}
            </Typography>
          )}

          {/* Show attachment status if requested */}
          {showAttachmentStatus && !isStudent && (
            <div>
              <Box className="attachment-status" mt={1}>
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "12px",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    color: isActiveInClass ? "#fff" : "#000",
                    backgroundColor: isActiveInClass ? "#4caf50" : "#bdbdbd",
                    minWidth: "60px",
                    textAlign: "center",
                  }}
                >
                  {isActiveInClass ? "Active" : "Inactive"}
                </Box>
              </Box>
              <Box className="attachment-status" mt={1}>
                {attachedAt && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Attached: {new Date(attachedAt).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            </div>
          )}
          {/* {quiz.file && (
              <Button
                variant="contained"
                color="primary"
                className="download-button"
                startIcon={<DownloadIcon />}
                href={`${process.env.REACT_APP_API_BASE_IMAGE_URL}${quiz.file}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download PDF
              </Button>
            )} */}
          {/* {
              !isStudent && (
                <Box className="quiz-action-buttons">
                    <Button
                        startIcon={<EditIcon />}
                        onClick={onEditClick}
                        className="quiz-action-button secondary-button"
                    >
                        Edit
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={onDeleteClick}
                        className="quiz-action-button delete-button"
                        >
                        Delete
                    </Button>
                </Box>
              )
            } */}
          <Box className="take-quiz-button-container">
            <Button
              variant="contained"
              startIcon={
                quiz.attempts_count > 0 ? (
                  <ReplayIcon />
                ) : (
                  <PlayCircleFilledWhiteIcon />
                )
              }
              onClick={() =>
                onTakeQuiz ? onTakeQuiz(quiz) : handleStartQuiz(quiz.id)
              }
              className="quiz-action-button take-quiz-button secondary-button"
            >
              {!isStudent
                ? "Assessment Preview"
                : quiz.attempts_count > 0
                ? "Retake Assessment"
                : "Take Assessment"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }
};

QuizCard.propTypes = {
  quiz: PropTypes.shape({
    id: PropTypes.number.isRequired,
    unique_identifier: PropTypes.string.isRequired,
    cover_image: PropTypes.string,
    file: PropTypes.string,
    created_at: PropTypes.string.isRequired,
    is_trial: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    class: PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
    }),
  }).isRequired,
  onEditClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onTakeQuiz: PropTypes.func,
  showAttachmentStatus: PropTypes.bool,
  isActiveInClass: PropTypes.bool,
  attachedAt: PropTypes.string,
};

export default QuizCard;
